import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const formatTimestamp = (value) => {
  if (!value) return "";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${String(value).replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const truncateText = (value, max = 120) => {
  if (!value) return "";
  const text = String(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3)}...`;
};

const formatJsonPreview = (value, limit = 4) => {
  if (!value) return { preview: "—", count: 0 };
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length === 0) return { preview: "—", count: 0 };
    const slice = parsed.slice(0, limit).join(", ");
    const ellipsis = parsed.length > limit ? ", …" : "";
    return { preview: `[${slice}${ellipsis}]`, count: parsed.length };
  } catch {
    return { preview: truncateText(value, 60), count: 0 };
  }
};

export default function AdminIngestWarnings() {
  const me = getCurrentUser();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const loadWarnings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const search = new URLSearchParams();
      if (status) search.set("status", status);
      search.set("limit", String(limit));
      search.set("offset", String(offset));
      const resp = await fetch(
        `${API_BASE}/api/admin/mrp-product-ingest-warnings?${search.toString()}`,
        { headers }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        throw new Error(data?.error || "Fetch failed");
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number.isFinite(data.total) ? data.total : 0);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load ingest warnings.");
    } finally {
      setLoading(false);
    }
  }, [me?.email, me?.role, status, limit, offset]);

  useEffect(() => {
    loadWarnings();
  }, [loadWarnings]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadWarnings();
    }, 60000);
    return () => {
      window.clearInterval(timer);
    };
  }, [loadWarnings]);

  const displayRows = useMemo(() => items.slice(), [items]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">Ingest Warnings</h1>
          <p className="text-sm text-slate-600">
            Warnings captured during MRP product ingestion from Wayli vessels.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {["OPEN", "ACKED", "RESOLVED", "ALL"].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[50, 100, 200, 500].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={loadWarnings}
            disabled={loading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="text-sm text-slate-600">
        Total warnings: <span className="font-semibold text-slate-800">{total}</span>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Provider Abbr</th>
              <th className="px-3 py-2 text-left font-semibold">Warning Type</th>
              <th className="px-3 py-2 text-left font-semibold">Count</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Last Seen</th>
              <th className="px-3 py-2 text-left font-semibold">Message</th>
              <th className="px-3 py-2 text-left font-semibold">Sample Vessel ID</th>
              <th className="px-3 py-2 text-left font-semibold">Sample Container ID</th>
              <th className="px-3 py-2 text-left font-semibold">Sample Vessel External</th>
              <th className="px-3 py-2 text-left font-semibold">Sample Container External</th>
              <th className="px-3 py-2 text-left font-semibold">Raw Vessel IDs</th>
              <th className="px-3 py-2 text-left font-semibold">Raw Container IDs</th>
            </tr>
          </thead>
          <tbody>
            {loading && displayRows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-slate-500">
                  Loading warnings...
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-slate-500">
                  No warnings found.
                </td>
              </tr>
            ) : (
              displayRows.map((row) => {
                const key = row.id || `${row.provider_customer_abbr}-${row.warning_type}`;
                const vesselPreview = formatJsonPreview(row.raw_vessel_ids_json);
                const containerPreview = formatJsonPreview(row.raw_container_ids_json);
                return (
                  <tr key={key}>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.provider_customer_abbr || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.warning_type || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.count_seen ?? "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.status || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {formatTimestamp(row.last_seen_at)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {truncateText(row.message)}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.sample_raw_vessel_id ?? "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.sample_raw_container_id ?? "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.sample_raw_vessel_external_id || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      {row.sample_raw_container_external_id || "-"}
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      <div className="flex items-center gap-2">
                        <span title={row.raw_vessel_ids_json || ""}>
                          {vesselPreview.count > 0
                            ? `${vesselPreview.preview} (n=${vesselPreview.count})`
                            : "—"}
                        </span>
                        {row.raw_vessel_ids_json ? (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-800"
                            onClick={() => navigator.clipboard.writeText(row.raw_vessel_ids_json)}
                          >
                            Copy
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="border-t px-3 py-2 align-top text-slate-700">
                      <div className="flex items-center gap-2">
                        <span title={row.raw_container_ids_json || ""}>
                          {containerPreview.count > 0
                            ? `${containerPreview.preview} (n=${containerPreview.count})`
                            : "—"}
                        </span>
                        {row.raw_container_ids_json ? (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:text-blue-800"
                            onClick={() => navigator.clipboard.writeText(row.raw_container_ids_json)}
                          >
                            Copy
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
